import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  AddMemberRequest,
  TenantMemberDto,
  TenantUserRole,
  TenantUserStatus,
} from '@/api/types';
import { STAFF_ROLES, memberKindOf } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { StatusChip } from '@/components/StatusChip';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RoleMenuItems } from '@/components/RolePicker';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

export function TenantMembers() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data.tenant;
  const { t } = useCopy();
  const snack = useSnackbar();
  const [rows, setRows] = useState<TenantMemberDto[] | null>(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [menuFor, setMenuFor] = useState<{ anchor: HTMLElement; row: TenantMemberDto } | null>(
    null,
  );
  const [confirm, setConfirm] = useState<{
    title: string;
    body: string;
    severity?: 'error' | 'warning' | 'primary';
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      setRows(await apiAdmin.members.list(tenant.id));
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      setRows([]);
    }
  }

  useEffect(() => {
    // `refresh()` only calls setState after an `await`, but the rule can't see
    // through the named call — run it inside an inline async IIFE so the effect
    // body holds no synchronous setState. Behavior is identical to `void refresh()`.
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  function safeError(err: unknown) {
    if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    else snack.error(t('errors.generic', 'Something went wrong.'));
  }

  // SASADM-0169 — the email to show: saas-auth's `displayEmail` (SASATH-0382) when present.
  function shownEmail(m: TenantMemberDto): string {
    return m.displayEmail ?? m.email;
  }

  async function changeRole(m: TenantMemberDto, role: TenantUserRole) {
    // Only staff memberships carry Owner (SASATH-0376), so count staff owners only.
    const owners =
      rows?.filter((r) => memberKindOf(r) === 'STAFF' && r.role === 'OWNER').length ?? 0;
    if (m.role === 'OWNER' && role !== 'OWNER' && owners <= 1) {
      setConfirm({
        title: t('members.lastOwnerWarning', 'This is the only Owner. Demote with care.'),
        body: t('members.confirm.disableBody', '', { email: shownEmail(m), tenant: tenant.name }),
        severity: 'warning',
        onConfirm: async () => {
          await doChangeRole(m, role);
          setConfirm(null);
        },
      });
      return;
    }
    await doChangeRole(m, role);
  }

  async function doChangeRole(m: TenantMemberDto, role: TenantUserRole) {
    try {
      await apiAdmin.members.setRole(tenant.id, m.membershipId, role, memberKindOf(m));
      snack.success(t('snackbar.memberUpdated', 'Member updated.'));
      await refresh();
    } catch (err) {
      safeError(err);
    }
  }

  async function setStatus(m: TenantMemberDto, status: TenantUserStatus) {
    try {
      await apiAdmin.members.setStatus(tenant.id, m.membershipId, status, memberKindOf(m));
      snack.success(t('snackbar.memberUpdated', 'Member updated.'));
      await refresh();
    } catch (err) {
      safeError(err);
    }
  }

  // Spec 0058 (saas-auth SASATH-0076) — the global per-membership SSO/GHL toggle
  // (`members.setOidcSso` + `TenantMemberDto.oidcSsoEnabled`) is removed: OIDC
  // access is now per-(member, client). The generic Members tab has no single
  // client in context, so the column is dropped here; per-client grants are
  // managed on the OIDC client's effective-members view (U2) and the reworked
  // OIDC SSO tab (U3). See the delivery's Architect notes (DECISION flag).

  async function remove(m: TenantMemberDto) {
    setBusy(true);
    try {
      await apiAdmin.members.remove(tenant.id, m.membershipId, memberKindOf(m));
      snack.success(t('snackbar.memberRemoved', 'Member removed.'));
      await refresh();
    } catch (err) {
      safeError(err);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  const sorted = rows
    ? [...rows].sort((a, b) => {
        const order: TenantUserRole[] = ['OWNER', 'SUPER_ADMIN', 'ADMIN', 'STAFF', 'USER', 'GUEST'];
        return (
          order.indexOf(a.role) - order.indexOf(b.role) ||
          (a.name ?? '').localeCompare(b.name ?? '')
        );
      })
    : null;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h2" component="h2">
          {t('members.title', 'Members')}
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenAdd(true)}>
          {t('members.add', 'Add member')}
        </Button>
      </Stack>
      {sorted === null ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('common.loading', 'Loading')}
          </Typography>
        </Paper>
      ) : sorted.length === 0 ? (
        <Alert severity="info">{t('members.empty', 'No members yet.')}</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t('common.menu', 'Name')}</TableCell>
                <TableCell>{t('members.email', 'Email')}</TableCell>
                <TableCell>{t('members.role', 'Role')}</TableCell>
                <TableCell>{t('members.status', 'Status')}</TableCell>
                <TableCell>{t('members.createdAt', 'Added')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((m) => (
                <TableRow key={m.membershipId}>
                  <TableCell>
                    <Avatar
                      {...(m.imageUrl ? { src: m.imageUrl } : {})}
                      sx={{ width: 32, height: 32 }}
                    >
                      {(m.name ?? shownEmail(m))[0]?.toUpperCase()}
                    </Avatar>
                  </TableCell>
                  <TableCell>{m.name ?? '—'}</TableCell>
                  <TableCell>
                    {m.email.endsWith('.entra.invalid') ? (
                      <Tooltip
                        title={t(
                          'members.entraTooltip',
                          'Microsoft account. The username shown is from Microsoft and is for display only.',
                        )}
                      >
                        <span>{shownEmail(m)}</span>
                      </Tooltip>
                    ) : (
                      shownEmail(m)
                    )}
                  </TableCell>
                  <TableCell>
                    {memberKindOf(m) === 'CUSTOMER' ? (
                      // A customer's role is not editable (SASATH-0376) — plain text, not a control.
                      <Typography variant="body2">
                        {t('members.roles.EMPLOYEE', 'Employee')}
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        select
                        value={m.role}
                        onChange={(e) => void changeRole(m, e.target.value as TenantUserRole)}
                        sx={{ minWidth: 130 }}
                      >
                        {(STAFF_ROLES as readonly TenantUserRole[]).includes(m.role) ? null : (
                          // A legacy staff role (e.g. SUPER_ADMIN) shows, but can't be chosen.
                          <MenuItem value={m.role} disabled>
                            {t(`members.roles.${m.role}`, m.role)}
                          </MenuItem>
                        )}
                        {STAFF_ROLES.map((r) => (
                          <MenuItem key={r} value={r}>
                            {t(`members.roles.${r}`, r)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip kind="memberStatus" value={m.status} />
                  </TableCell>
                  <TableCell>{new Date(m.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label={t('common.actions', 'Actions')}
                      onClick={(e) => setMenuFor({ anchor: e.currentTarget, row: m })}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Menu
        open={Boolean(menuFor)}
        anchorEl={menuFor?.anchor ?? null}
        onClose={() => setMenuFor(null)}
      >
        {menuFor?.row.status === 'ACTIVE' ? (
          <MenuItem
            onClick={() => {
              const row = menuFor.row;
              setMenuFor(null);
              setConfirm({
                title: t('members.confirm.disableTitle', 'Disable member?'),
                body: t('members.confirm.disableBody', '', {
                  email: shownEmail(row),
                  tenant: tenant.name,
                }),
                severity: 'warning',
                onConfirm: async () => {
                  await setStatus(row, 'DISABLED');
                  setConfirm(null);
                },
              });
            }}
          >
            {t('members.actions.disable', 'Disable')}
          </MenuItem>
        ) : null}
        {menuFor?.row.status === 'DISABLED' ? (
          <MenuItem
            onClick={() => {
              const row = menuFor.row;
              setMenuFor(null);
              void setStatus(row, 'ACTIVE');
            }}
          >
            {t('members.actions.enable', 'Enable')}
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            const row = menuFor!.row;
            setMenuFor(null);
            setConfirm({
              title: t('members.confirm.removeTitle', 'Remove member?'),
              body: t('members.confirm.removeBody', '', {
                email: shownEmail(row),
                tenant: tenant.name,
              }),
              severity: 'error',
              onConfirm: () => remove(row),
            });
          }}
        >
          {t('members.actions.remove', 'Remove from tenant')}
        </MenuItem>
      </Menu>
      <AddMemberDialog
        open={openAdd}
        tenantId={tenant.id}
        onClose={() => setOpenAdd(false)}
        onAdded={() => {
          setOpenAdd(false);
          void refresh();
          snack.success(t('snackbar.memberAdded', 'Member added.'));
        }}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        severity={confirm?.severity ?? 'primary'}
        confirmLabel={t('common.yes', 'Yes')}
        busy={busy}
        onConfirm={() => void confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
    </Box>
  );
}

interface AddMemberDialogProps {
  open: boolean;
  tenantId: number;
  onClose: () => void;
  onAdded: () => void;
}

interface AddMemberFormValues {
  email: string;
  role: TenantUserRole;
}

function AddMemberDialog({ open, tenantId, onClose, onAdded }: AddMemberDialogProps) {
  const { t } = useCopy();
  const [error, setError] = useState<ApiError | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  const [pending, setPending] = useState(false);
  const form = useForm<AddMemberFormValues>({
    defaultValues: { email: '', role: 'USER' },
  });
  const roleItems = RoleMenuItems();

  // Reset the form fields when the dialog opens. `form.reset` targets the
  // react-hook-form store (not React state), so it stays in the effect.
  useEffect(() => {
    if (open) {
      form.reset({ email: '', role: 'USER' });
    }
  }, [open, form]);

  // Clear the inline error on the open transition via a render-phase state
  // adjustment (React's "adjust state while rendering" pattern) instead of a
  // synchronous setState in an effect — same result, no inter-render error flash.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setError(null);
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setPending(true);
    setError(null);
    const payload: AddMemberRequest = { email: values.email.trim(), role: values.role };
    try {
      await apiAdmin.members.add(tenantId, payload);
      onAdded();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'USER_NOT_FOUND') {
          form.setError('email', { message: t('errors.codes.USER_NOT_FOUND', '') });
        } else {
          setError(err);
        }
      }
    } finally {
      setPending(false);
    }
  });

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('members.add', 'Add member')}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={error} />
        <Box component="form" onSubmit={onSubmit} id="add-member-form" noValidate>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('members.email', 'Email')}
              type="email"
              required
              {...form.register('email', { required: true })}
              error={Boolean(form.formState.errors.email)}
              helperText={
                form.formState.errors.email?.message ??
                t(
                  'members.addHelper',
                  'Adds someone who already has an account. For Microsoft sign-in, use Invitations.',
                )
              }
            />
            <Controller
              control={form.control}
              name="role"
              render={({ field }) => (
                <TextField {...field} select fullWidth label={t('members.role', 'Role')}>
                  {roleItems}
                </TextField>
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button form="add-member-form" type="submit" variant="contained" disabled={pending}>
          {t('members.add', 'Add member')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
